import { useCallback, useEffect, useMemo, useState } from "react";
import { Auth } from "shared/@auth/Auth";
import { ENVIRONMENT_CONFIG } from "shared/globals";
import createNotification from "shared/@utils/createNotification";

const defaultYear = 2026;
const yearOptions = [2025, 2026, 2027];
const resolvedApiBase =
  ENVIRONMENT_CONFIG.newAPIBase ||
  (ENVIRONMENT_CONFIG.apiBase || "").replace(/\/api\/?$/, "");
const eventOverrideBaseUrl = `${resolvedApiBase.replace(
  /\/$/,
  ""
)}/v1/membership/event-override`;

export function useEventOverride(iboNumber) {
  const { makeAuthedRequest, account } = Auth.useContainer();

  const [filters, setFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedEventType, setSelectedEventType] = useState("");
  const [selectedEventName, setSelectedEventName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [displayBadge, setDisplayBadge] = useState(true);
  const [noteValue, setNoteValue] = useState("");

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const fetchFilters = useCallback(async () => {
    setFiltersLoading(true);
    try {
      const res = await makeAuthedRequest(
        `${eventOverrideBaseUrl}/filters/year/${selectedYear}`
      );
      if (!res.ok) {
        throw new Error("Failed to load event override filters.");
      }
      const data = await res.json();
      setFilters(Array.isArray(data) ? data : []);
    } catch (error) {
      setFilters([]);
      createNotification({
        type: "danger",
        title: "Event Override",
        message: error.message,
        autoDismiss: true,
      });
    } finally {
      setFiltersLoading(false);
    }
  }, [makeAuthedRequest, selectedYear]);

  const fetchGroups = useCallback(async () => {
    if (!iboNumber) {
      return;
    }
    setGroupsLoading(true);
    try {
      const res = await makeAuthedRequest(
        `${eventOverrideBaseUrl}/groups/ibo/${iboNumber}`
      );
      if (!res.ok) {
        throw new Error("Failed to load event override groups.");
      }
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      setGroups([]);
      createNotification({
        type: "danger",
        title: "Event Override",
        message: error.message,
        autoDismiss: true,
      });
    } finally {
      setGroupsLoading(false);
    }
  }, [iboNumber, makeAuthedRequest]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    setSelectedEventType("");
    setSelectedEventName("");
    setSelectedGroupId("");
  }, [filters]);

  const eventTypes = useMemo(() => {
    return [...new Set(filters.map((item) => item.eventType).filter(Boolean))];
  }, [filters]);

  const eventNames = useMemo(() => {
    if (!selectedEventType) {
      return [];
    }
    return [
      ...new Set(
        filters
          .filter((item) => item.eventType === selectedEventType)
          .map((item) => item.eventName)
          .filter(Boolean)
      ),
    ];
  }, [filters, selectedEventType]);

  const groupsForSelection = useMemo(() => {
    if (!selectedEventType || !selectedEventName) {
      return [];
    }
    const filtered = filters.filter(
      (item) =>
        item.eventType === selectedEventType &&
        item.eventName === selectedEventName
    );

    const byId = new Map();
    filtered.forEach((item) => {
      if (!byId.has(item.groupID)) {
        byId.set(item.groupID, item.groupName);
      }
    });

    return Array.from(byId.entries()).map(([groupID, groupName]) => ({
      groupID,
      groupName,
    }));
  }, [filters, selectedEventType, selectedEventName]);

  useEffect(() => {
    if (!selectedEventType && eventTypes.length === 1) {
      setSelectedEventType(eventTypes[0]);
    }
  }, [eventTypes, selectedEventType]);

  useEffect(() => {
    if (!selectedEventName && eventNames.length === 1) {
      setSelectedEventName(eventNames[0]);
    }
  }, [eventNames, selectedEventName]);

  useEffect(() => {
    if (!selectedGroupId && groupsForSelection.length === 1) {
      setSelectedGroupId(String(groupsForSelection[0].groupID));
    }
  }, [groupsForSelection, selectedGroupId]);

  const handleAction = useCallback(
    async (action, groupIdOverride = null) => {
      const createdBy = account?.username || account?.name;
      const groupId = groupIdOverride ?? selectedGroupId;

      if (!groupId) {
        createNotification({
          type: "info",
          title: "Event Override",
          message: "Please select a group before performing this action.",
          autoDismiss: true,
        });
        return;
      }

      if (!createdBy) {
        createNotification({
          type: "danger",
          title: "Event Override",
          message: "Unable to determine the current user.",
          autoDismiss: true,
        });
        return;
      }

      try {
        const res = await makeAuthedRequest(eventOverrideBaseUrl, {
          method: "POST",
          body: JSON.stringify({
            ibo: iboNumber,
            groupId: Number(groupId),
            createdBy,
            notes: noteValue,
            action,
          }),
        });

        const message = await res.text();

        if (!res.ok) {
          throw new Error(message || "Failed to update event override.");
        }

        createNotification({
          type: "success",
          title: "Event Override",
          message: message || "Update submitted successfully.",
          autoDismiss: true,
        });

        await fetchGroups();
      } catch (error) {
        createNotification({
          type: "danger",
          title: "Event Override",
          message: error.message,
          autoDismiss: true,
        });
      }
    },
    [
      account,
      fetchGroups,
      iboNumber,
      makeAuthedRequest,
      noteValue,
      displayBadge,
      selectedGroupId,
    ]
  );

  const getStatusClass = useCallback((statusInGroup) => {
    const status = (statusInGroup || "").toUpperCase();
    if (status === "GREEN") {
      return "statusIncluded";
    }
    if (status === "RED") {
      return "statusRevoked";
    }
    return "statusOverride";
  }, []);

  return {
    yearOptions,
    filtersLoading,
    groupsLoading,
    selectedYear,
    selectedEventType,
    selectedEventName,
    selectedGroupId,
    displayBadge,
    noteValue,
    eventTypes,
    eventNames,
    groupsForSelection,
    groups,
    setSelectedYear,
    setSelectedEventType,
    setSelectedEventName,
    setSelectedGroupId,
    setDisplayBadge,
    setNoteValue,
    handleAction,
    getStatusClass,
  };
}
